import React from 'react';
import {Alert, Box, Button, Chip, DialogActions, DialogContent, DialogTitle, Paper, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {getSettingsColors, SettingsEmptyState} from '../settings/SettingsDesign';
import {SettingsDialog} from '../settings/SettingsDialog';
import CopyContentToClipboard from '../utils/CopyContentToClipboard';
import Spinner from '../spinner/Spinner';
import {UploadFileOutlined} from '@mui/icons-material';

export function WebsiteZipFilePicker({file, onChange, disabled = false}) {
    const input = React.useRef(null);
    const labelId = React.useId();
    return <Box sx={{minWidth: 0}}>
        <Typography id={labelId} sx={{fontSize: 12, color: 'text.secondary', mb: 1}}>ZIP file *</Typography>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0}}>
            <Button type="button" variant="outlined" color="inherit" startIcon={<UploadFileOutlined/>} disabled={disabled}
                onClick={() => input.current?.click()} sx={{flexShrink: 0, px: 1.5, borderColor: 'divider'}}>Choose ZIP file</Button>
            <Box component="span" role="status" title={file?.name} sx={{flex: 1, minWidth: 0, fontSize: 13,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: file ? 'text.primary' : 'text.secondary'}}>
                {file?.name || 'No file selected'}
            </Box>
            <input ref={input} type="file" accept=".zip" hidden disabled={disabled} aria-labelledby={labelId}
                onChange={event => {onChange(event); event.target.value = '';}}/>
        </Box>
    </Box>;
}

export function WebsiteSectionHeader({title, description, children}) {
    return <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2.5}}>
        <Box sx={{minWidth: 0, flex: '1 1 300px'}}>
            <Typography component="h2" sx={{fontSize: 18, fontWeight: 600}}>{title}</Typography>
            <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.75, lineHeight: 1.7, whiteSpace: 'normal'}}>{description}</Typography>
        </Box>
        {children}
    </Box>;
}

export function WebsiteTableCard({title, count, rowCount = 0, selectedCount, actions, children}) {
    const gridHeight = Math.max(260, 48 + Math.min(rowCount, 8) * 55);
    return <Paper elevation={0} sx={{minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: '12px',
        overflow: 'hidden', bgcolor: theme => getSettingsColors(theme).card}}>
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2.5, py: 2}}>
            <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1}}>
                <Typography sx={{fontSize: 16, fontWeight: 600}}>{title}</Typography>
                {count != null && <Chip label={count} size="small" sx={{height: 24, fontWeight: 600,
                    bgcolor: theme => getSettingsColors(theme).inset, color: 'text.secondary'}}/>}
                {selectedCount > 0 && <Typography sx={{fontSize: 12, color: 'text.secondary'}}>{selectedCount} selected</Typography>}
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, '& .MuiButton-root': {minHeight: 35, borderRadius: '8px', textTransform: 'none', boxShadow: 'none'},
                '& .MuiButton-outlinedInherit': {borderColor: 'divider', color: 'text.primary'}}}>{actions}</Box>
        </Box>
        <Box sx={theme => ({position: 'relative', minWidth: 0, borderTop: '1px solid', borderColor: 'divider',
            '& .website-data-grid': {
                height: `clamp(260px, calc(100dvh - 360px), ${gridHeight}px)`, width: '100%', border: 0, fontSize: 13,
                fontFamily: 'inherit', colorScheme: theme.palette.mode,
                '--rdg-color-scheme': theme.palette.mode,
                '--rdg-color': theme.palette.text.primary,
                '--rdg-background-color': getSettingsColors(theme).card,
                '--rdg-header-background-color': getSettingsColors(theme).inset,
                '--rdg-header-draggable-background-color': getSettingsColors(theme).inset,
                '--rdg-border-color': theme.palette.divider,
                '--rdg-row-hover-background-color': theme.palette.background.rowHover,
                '--rdg-row-selected-background-color': alpha(theme.palette.primary.main, 0.1),
                '--rdg-row-selected-hover-background-color': alpha(theme.palette.primary.main, 0.16),
                '--rdg-selection-color': theme.palette.primary.main,
                '--rdg-checkbox-color': theme.palette.primary.main,
                '--rdg-checkbox-focus-color': theme.palette.primary.main
            },
            '& .rdg-header-row': {fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary},
            '& .rdg-cell': {borderInlineEnd: 0, px: 2, boxShadow: 'none'},
            '& .rdg-row': {cursor: 'pointer'},
            '& .MuiChip-root': {borderRadius: '6px', height: 24, fontSize: 11},
            '& [role="gridcell"][aria-selected="true"], & [role="columnheader"][aria-selected="true"]': {outline: 'none'}
        })}>{children}</Box>
    </Paper>;
}

export function WebsiteEmptyState({icon, title, description}) {
    return <Box sx={{position: 'absolute', top: 48, left: 0, right: 0}}><SettingsEmptyState icon={icon} title={title} description={description}/></Box>;
}

export function WebsiteDialog({loading, children, sx = [], ...props}) {
    return <SettingsDialog {...props} sx={[{
        '& .MuiDialogContent-root': {position: 'relative'},
        '& .MuiDialogTitle-root + .MuiDialogContent-root': {pt: 1.5},
        '& .website-form-fields': {display: 'grid', gap: 2,
            '& > .MuiBox-root': {p: 0, minWidth: 0}, '& .MuiTextField-root': {my: 0}},
        '& .MuiStepper-root': {p: 2, mb: 3, borderRadius: '10px', bgcolor: theme => getSettingsColors(theme).inset, overflowX: 'auto'},
        '& .MuiStepLabel-label': {fontSize: 12}, '& .MuiStepConnector-line': {borderColor: 'divider'},
        '& .MuiAlert-root': {borderRadius: '10px', fontSize: 13, lineHeight: 1.7},
        '& .MuiAlert-filledWarning': {bgcolor: theme => alpha(theme.palette.warning.main, 0.14),
            color: theme => theme.palette.mode === 'dark' ? theme.palette.warning.light : '#8a4500', '& .MuiAlert-icon': {color: 'inherit'}},
        '& .MuiFormControlLabel-root': {ml: -1, mr: 0, '& .MuiTypography-root': {fontSize: 13}},
        '& .MuiButton-root': {textTransform: 'none', minHeight: 36, boxShadow: 'none'}
    }, ...(Array.isArray(sx) ? sx : [sx])]}>
        {loading && <Spinner size={32} overComponentBox/>}
        {children}
    </SettingsDialog>;
}

export function WebsiteDeleteDialog({question, text, confirm, confirmLabel}) {
    return <WebsiteDialog open fullWidth maxWidth="xs" onClose={() => confirm(false)} aria-labelledby="website-delete-title">
        <DialogTitle id="website-delete-title">{question}</DialogTitle>
        <DialogContent><Typography sx={{fontSize: 14, color: 'text.secondary', lineHeight: 1.7}}>{text}</Typography></DialogContent>
        <DialogActions>
            <Button color="inherit" onClick={() => confirm(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={() => confirm(true)}>{confirmLabel}</Button>
        </DialogActions>
    </WebsiteDialog>;
}

export function WebsiteCnameRecords({record}) {
    return <>
        <Typography sx={{fontSize: 13, color: 'text.secondary', mb: 2, lineHeight: 1.7}}>
            Add this CNAME record to your domain’s DNS configuration to verify ownership and issue your SSL certificate.
        </Typography>
        <Box sx={{p: 2, borderRadius: '10px', bgcolor: theme => getSettingsColors(theme).inset, display: 'grid', gap: 2,
            '& .MuiOutlinedInput-root': {fontFamily: 'monospace', fontSize: 12},
            '& .MuiBox-root': {minWidth: 0}}}>
            <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                <Typography sx={{fontSize: 13, color: 'text.secondary'}}>Record type</Typography><Chip label="CNAME" size="small" variant="outlined"/>
            </Box>
            <CopyContentToClipboard label="Record name" copyText={record?.name}/>
            <CopyContentToClipboard label="Record value" copyText={record?.value}/>
        </Box>
        <Alert severity="warning" variant="filled" sx={{mt: 3}}>
            Keep this DNS record in place to allow AWS Certificate Manager to issue and renew your certificate.
            Remove it at any time to revoke permission.
        </Alert>
    </>;
}
